import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useLocation} from 'react-router'
import {useAuth0} from '@auth0/auth0-react'
import {toast} from 'react-toastify'
import {Modal} from '@thryvlabs/maverick'
import {useAuthClient} from '../../../utils/use-auth-client'
import {useUserFeatures} from '../../../context/user-features-context'
import {applyProductRulesBeforeLoading} from '../../../features/CatalogGrid/utils/productPreloadRules'
import {fetchKeapPartnerCatalog} from '../../../utils/fetch-keap-partner-catalog'
import {
  hasBlockingPastDueInvoice,
  SHOW_INVOICE_INFO_FOR_PAST_DUE,
} from '../../../utils/past-due-invoice'
import {InvoiceInformation} from '../../../components/account-information-components/invoice-information'
import {buildNewLocationOrderState} from '../AccountsTabs/newLocationOrderState'

// ─── Redux ────────────────────────────────────────────────────────────────────
import {
  goNext,
  goBack,
  setStep,
  completeCurrentAndSetStep,
  resetJourney,
  selectCurrentStep,
  selectCurrentStepDef,
  selectCanGoBack,
  selectCanGoNext,
  selectCompletedSteps,
  selectTransactionTypeState,
  selectCountrySelectState,
  selectEmailLookupState,
  selectViewResultsEmail,
  setViewResultsEmail,
  setEmailLookupState,
  selectParents,
  updateParentField,
  STEPS,
} from '../../../app-store/AccountSetupSlice'
import {
  setAccountInfo,
  initLocations,
  setProductConfigInfo,
  resetAll as resetPurchaseLink,
  selectAccountInfo as selectPurchaseLinkAccountInfo,
  selectHasCartItems,
  selectLocations,
  selectOrderId,
  selectOrderMode,
  selectSelectedLocationId,
  selectIsUpgrade,
  setOrderId,
  setRepInfo,
  setSalesAttributionFields,
} from '../../../app-store/PurchaseLinkSlice'
import {selectEffectiveRole} from '../../../app-store/selector'
import {resetInfluencerName} from '../../../app-store/kpGlobalStateSlice'
import {shouldUseLegacyPartnerOpsFlow} from '../../../utils/account-management-navigation'
import {buildCreateOrderPayload} from './utils/buildCreateOrderPayload'
import {getSchedulingLocations} from './utils/getSchedulingLocations'
import {
  parseLocationIdFromInteraction,
  callTypeForAppointmentRow,
} from './hooks/useOnboardingScheduling'

// ─── Shared components ────────────────────────────────────────────────────────
import {Button} from '../../shared/Button'
import styled from '@emotion/styled'
import {keyframes} from '@emotion/react'
import {theme} from '../../theme'

// ─── Step components ──────────────────────────────────────────────────────────
import {MyAccounts} from '../AccountsTabs/MyAccounts'
import {AllAccounts} from '../AccountsTabs/AllAccounts'
import {EmailLookupStep} from './components/EmailLookupStep'
import {CatalogSelectionStep} from './components/CatalogSelectionStep'
import {SchedulingStep} from './components/SchedulingStep'
import {PaymentDetailsStep} from './components/PaymentDetailsStep'

// ─── Layout ───────────────────────────────────────────────────────────────────
import {Stepper} from './components/Stepper'

// ─── Styles ───────────────────────────────────────────────────────────────────
import {
  PageWrapper,
  ContentArea,
  StepCard,
  FooterRow,
  FooterCenter,
  ContentAreaWrapper,
} from './AccountSetup.styles'
import {AccountInfo} from '../AccountInfo/AccountInfo'
import {getTaxIdentifier} from '../AccountInfo/utils/taxIdentifier'
// import {AccountInfoSetup} from '../AccountInfoSetup'

// ─── Page-level loading overlay ───────────────────────────────────────────────
const spin = keyframes`
  to { transform: rotate(360deg); }
`
const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.space[4]};
  z-index: ${theme.zIndex.overlay};
`
const SpinnerRing = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite;
`
const SpinnerLabel = styled.p`
  color: #ffffff;
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.medium};
  margin: 0;
`

// ─── Step registry ────────────────────────────────────────────────────────────

const STEP_REGISTRY = {
  'email-lookup': {
    component: EmailLookupStep,
    getProps: () => ({}),
  },
  // 'transaction-type': {
  //   component: TransactionType,
  //   getProps: () => ({}),
  // },
  'account-info-setup': {
    component: AccountInfo,
    getProps: () => ({}),
  },
  'catalog-selection': {
    component: CatalogSelectionStep,
    getProps: () => ({}),
  },
  scheduling: {
    component: SchedulingStep,
    getProps: () => ({}),
  },
  'payment-details': {
    component: PaymentDetailsStep,
    getProps: () => ({}),
  },
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AccountSetupPage({onExit, keapPartnerId: keapPartnerIdProp}) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const currentStep = useSelector(selectCurrentStep)
  const step = useSelector(selectCurrentStepDef)
  const completedSteps = useSelector(selectCompletedSteps)
  const canGoBack = useSelector(selectCanGoBack)

  const {selectedTransactionType} = useSelector(selectTransactionTypeState)
  const isMultiLocation = selectedTransactionType === 'multiple-locations'
  const {selectedCountry} = useSelector(selectCountrySelectState)
  const {verifiedEmail, meta: emailLookupMeta} = useSelector(
    selectEmailLookupState,
  )
  const viewResultsThryvId = emailLookupMeta?.thryvId ?? null
  const viewResultsAccountCodes = emailLookupMeta?.partnerAccountCodes ?? []
  const viewResultsEmail = useSelector(selectViewResultsEmail)
  const parents = useSelector(selectParents)

  const client = useAuthClient()
  const {user} = useAuth0()
  const effectiveRole = useSelector(selectEffectiveRole)
  const effectiveRoles = Array.isArray(effectiveRole)
    ? effectiveRole
    : [effectiveRole].filter(Boolean)
  const isAdminRole = effectiveRoles.some(
    role => String(role).toLowerCase() === 'admin',
  )
  const userFeatures = useUserFeatures()
  // A Keap/PartnerOps session is the source of truth for catalog selection.
  // Some partner profiles legitimately have no user_metadata.keapPartnerId
  // yet; relying on that optional attribution value would incorrectly load
  // the generic Core OOE inventory and leave the KP catalog empty.
  const isKeapPartnerWorkflow = (effectiveRole ?? []).some(role =>
    ['KeapPartner', 'InfluencerOps', 'PartnerOps'].includes(role),
  )
  // GEP-7133 / FULSAS-13599: market-split gate for the TGP upgrade flow —
  // same derivation used throughout (e.g. useCatalogCart.js,
  // ProductPlanSelector/index.js). Passed into getSchedulingLocations below
  // so shouldRequireOnboardingCall's isTgpUpgradeEnabled option (see
  // scheduling-call-rules.js) actually takes effect for this flow.
  const isTgpUpgradeEnabled =
    (['AU', 'NZ'].includes(selectedCountry) &&
      Boolean(userFeatures?.Enable_AU_NZ_TGP_Upgrade)) ||
    (selectedCountry === 'US' && Boolean(userFeatures?.Enable_US_TGP_Upgrade))

  // keapPartnerId resolution: the in-pane Control Center host passes it as a prop
  // (resolved from userData.user_metadata.keapPartnerId). state.user.keapPartnerId is
  // only populated from a URL ?pac= param / sessionStorage, which are absent in the
  // in-pane flow — so it's null here. Fall back through prop → store → user_metadata so
  // the creation-time partner association is always attributed correctly.
  const keapPartnerIdFromStore = useSelector(state => state.user.keapPartnerId)
  const keapPartnerId =
    keapPartnerIdProp ||
    keapPartnerIdFromStore ||
    user?.user_metadata?.keapPartnerId ||
    null

  const [isSaving, setIsSaving] = useState(false)
  // True while AccountInfo's Google address check (and any address-suggestion
  // confirmation) is running. Kept separate from isSaving so the "Creating
  // account…" overlay below doesn't cover AccountInfo's own "Validating
  // Address" overlay during that phase.
  const [isValidatingAddress, setIsValidatingAddress] = useState(false)
  const [showPastDueInvoiceModal, setShowPastDueInvoiceModal] = useState(false)
  const [pastDueThryvId, setPastDueThryvId] = useState(null)

  const location = useLocation()
  const prefillAppliedRef = useRef(false)

  // Seeds PurchaseLinkSlice's salesRepId/userRole once per journey — this
  // page is the single entry point every "Create Purchase Link" trigger
  // (AllAccounts/MyAccounts' several handleOrder/handleSaveAndProceed call
  // sites) navigates to, so setting it here instead of at each of those
  // call sites covers all of them without duplicating the wiring. Mirrors
  // ProposalCreationComponent's identical sourcing of proposalInfo's own
  // salesRepId/userRole. Re-dispatches if user/effectiveRole change (e.g.
  // Auth0's user resolving after this page has already mounted).
  useEffect(() => {
    dispatch(
      setRepInfo({
        salesRepId: user?.email ?? null,
        userRole: effectiveRole?.[0] ?? null,
      }),
    )
  }, [user, effectiveRole, dispatch])

  // When navigating from the Past Demos "New Order" button, location.state carries
  // prefillFromDemo with customer fields from the demo record. Once the AccountInfo
  // tree initialises (parents[0] exists), dispatch updateParentField for each field.
  // The ref guard prevents re-running when the rep edits a field (each edit updates
  // parents in Redux which would otherwise retrigger this effect).
  useEffect(() => {
    const prefill = location.state?.prefillFromDemo
    if (!prefill || !parents?.length || prefillAppliedRef.current) return
    prefillAppliedRef.current = true
    const parentId = parents[0]?.id
    Object.entries(prefill).forEach(([field, value]) => {
      if (value != null && value !== '') {
        dispatch(updateParentField({parentId, field, value}))
      }
    })
  }, [parents, location.state, dispatch])

  // handleSingleLocNext navigates away (unmounting this page) before its `finally`
  // runs, so guard the trailing setState to avoid a React "update on unmounted
  // component" warning.
  const isMountedRef = useRef(true)
  useEffect(() => {
    // The influencer name is global (kpGlobalState) and outlives the wizard, so
    // clear it on entry too — otherwise a name from a previous order shows up
    // pre-filled on this account's catalog step.
    dispatch(resetInfluencerName())
    return () => {
      isMountedRef.current = false
      dispatch(resetJourney())
      dispatch(resetPurchaseLink())
      dispatch(resetInfluencerName())
    }
  }, [])

  const accountInfoRef = useRef()
  // ── canProceed — driven entirely by Redux, no local state ─────────────────
  //
  // selectCanGoNext checks each step's required Redux state:
  //   email-lookup     → emailLookupState.verifiedEmail must be set
  //   transaction-type → transactionTypeState.selectedTransactionType must be set
  //
  // Now the Redux selector is the single source of truth — no local state,
  // no timing issues, no Back navigation edge cases.
  const canProceed = useSelector(selectCanGoNext)

  // onReady is still passed to EmailLookup which manages readiness
  // before dispatching to Redux (it calls setEmailLookupState on success).
  // For TransactionType — which dispatches immediately on mount — onReady
  // is not needed but accepted as a no-op to keep the API consistent.
  const handleReady = useCallback(() => {}, [])

  // ── Resolve active step ───────────────────────────────────────────────────

  const registry = STEP_REGISTRY[step?.id]
  const Component = registry?.component
  const stepProps = registry?.getProps() ?? {}

  // ── Footer visibility ──────────────────────────────────────────────────────
  // Email step: only Cancel shown until email-lookup is completed.
  // All other steps: Cancel | < Back  Continue >

  const isEmailStep = step?.id === 'email-lookup'
  const emailStepCompleted = completedSteps.includes('email-lookup')
  const isAccountInfoStep = step?.id === 'account-info-setup'

  // const showNavButtons =
  //   emailStepCompleted || (isAccountInfoStep && !isMultiLocation)
  // const showNavButtons = !isEmailStep || emailStepCompleted
  const showNavButtons = isEmailStep && emailStepCompleted
  const showSinglelocaContinue = isAccountInfoStep && !isMultiLocation

  const handleBackFromResults = useCallback(() => {
    dispatch(setViewResultsEmail(null))
    dispatch(setEmailLookupState({email: null, accountType: null, meta: null}))
  }, [dispatch])

  const handleAddAccountFromResults = useCallback(
    email => {
      dispatch(setEmailLookupState({email, accountType: null, meta: null}))
      dispatch(goNext())
      dispatch(setViewResultsEmail(null))
    },
    [dispatch],
  )

  const hasCartItems = useSelector(selectHasCartItems)
  const purchaseLinkAccountInfo = useSelector(selectPurchaseLinkAccountInfo)
  const purchaseLinkLocations = useSelector(selectLocations)
  const orderMode = useSelector(selectOrderMode)
  const selectedLocationId = useSelector(selectSelectedLocationId)
  const isUpgradeJourney = useSelector(selectIsUpgrade)
  const orderId = useSelector(selectOrderId)
  const [isCreatingOrder, setIsCreatingOrder] = useState(false)

  const handleCancel = async () => {
    // Continue on Catalog Selection already created the order (status
    // 'Pending' — see handleCatalogContinue). Abandoning from here on out
    // must not leave it dangling — mirrors new-order.js's updateOrder5Status
    // 'cancel-order' context, which marks the legacy flow's order Cancelled
    // on the same kind of exit. A booked onboarding call must also be
    // released — otherwise the calendar hold and the scheduled_appointment
    // row both survive an abandoned order.
    //
    // Once the payment link has gone out (order 'Submitted') the customer can
    // still pay it, and a 'Completed' order has already been paid — in both
    // cases the order and its appointments must be left alone, so only the
    // Redux reset + navigation below run. The status is read from the server
    // rather than the Payment Details timeline, which can lag behind the
    // socket/poll or be unmounted on other steps.
    let skipOrderCancel = false
    if (orderId) {
      try {
        const orderResult = await client(
          `ordercapture/services/getOrderDetails/${orderId}`,
        )
        const currentOrder = orderResult?.response
        if (!orderResult?.success || !currentOrder?.order_status) {
          throw new Error('Order status unavailable')
        }
        skipOrderCancel = ['Submitted', 'Completed'].includes(
          currentOrder.order_status,
        )
      } catch (err) {
        // Status unknown — don't risk cancelling a paid order or releasing its
        // appointments. Stay on the page so the rep can retry.
        console.error('Unable to check order status for cancel', err)
        toast.error('Unable to cancel the order. Please try again.')
        return
      }
    }
    if (orderId && !skipOrderCancel) {
      try {
        const {response: appointmentRows} = await client(
          `ordercapture/services/getscheduledAppointments/${orderId}`,
        )
        const activeAppointments = (appointmentRows ?? []).filter(
          row => row.schedule_status !== 'Cancelled',
        )
        await Promise.all(
          activeAppointments.map(async row => {
            const locationId = parseLocationIdFromInteraction(row.interaction)
            const location = purchaseLinkLocations.find(
              l => l.locationId === locationId,
            )
            const country =
              location?.country || purchaseLinkAccountInfo?.country || 'US'
            // Kickoff vs Thryv Success Services calls release through
            // different DELETE routes — pick by the row's appointment_type.
            const callType = callTypeForAppointmentRow(row)
            try {
              await client(callType.cancelEndpoint, {
                data: {
                  type: callType.appointmentTypeKey,
                  demoApptId: row.appt_id,
                  country,
                },
                method: 'DELETE',
              })
            } catch (err) {
              console.error('Unable to release scheduled appointment', err)
            }
            try {
              await client(
                'ordercapture/services/updateScheduledAppointmentStatus',
                {
                  data: {appt_id: row.appt_id, schedule_status: 'Cancelled'},
                  method: 'PUT',
                },
              )
            } catch (err) {
              console.error('Unable to mark appointment cancelled', err)
            }
          }),
        )
      } catch (err) {
        console.error('Unable to fetch scheduled appointments for cancel', err)
      }
      try {
        await client('ordercapture/services/updateOrderStatus', {
          data: {order_id: orderId, order_status: 'Cancelled'},
          method: 'PUT',
        })
      } catch (err) {
        console.error('Unable to cancel abandoned order', err)
      }
    }
    handleExit()
  }

  // Leaves the wizard without touching the order — Redux reset + navigation
  // only. Used by Payment Details' Exit once a partner link has been generated
  // (the order must stay as-is for the customer's link), and as the tail of
  // handleCancel.
  const handleExit = () => {
    dispatch(resetJourney())
    // In-pane host (e.g. Keap Partner Control Center) passes onExit so we return
    // to the accounts view without a route change. Standalone routes go back.
    if (onExit) {
      onExit()
      return
    }
    // "Create Purchase Link" (AllAccounts/MyAccounts) stamps returnTo/returnSearch
    // on the navigate() that brought us here, so Cancel can land back on the same
    // search results instead of a blank All/My Accounts page — navigate(-1) would
    // remount that page fresh with no memory of the prior search (its query is
    // local useState, not persisted anywhere else). Falls back to browser
    // history for entry points that don't set this (e.g. a brand-new account).
    const {returnTo, returnSearch} = location.state ?? {}
    if (returnTo) {
      navigate(returnTo, {
        state: {context: 'returnFromAccountSetup', email: returnSearch},
      })
      return
    }
    navigate(-1)
  }
  const isAccountInfoComponent = step?.id === 'account-info-setup'
  // Only this step breaks out of ContentArea's fixed or floating width.
  const isCatalogStep = step?.id === 'catalog-selection'

  const handleCatalogContinue = async () => {
    setIsCreatingOrder(true)
    try {
      const payload = buildCreateOrderPayload({
        accountInfo: purchaseLinkAccountInfo,
        locations: purchaseLinkLocations,
        orderMode,
        selectedLocationId,
        isUpgrade: isUpgradeJourney,
        salesRepId: isKeapPartnerWorkflow ? user?.email || null : null,
      })
      // createOrder rejects an order with no lines ("At least one order line
      // is required") — happens when nothing new was added (existing plans
      // are skipped as alreadyIncluded), so stop here with a friendly message.
      if (!payload.lines.length) {
        toast.error(
          'Woops! Your cart is empty. Please add products to continue.',
        )
        return
      }
      const response = await client('ordercapture/services/createOrder', {
        data: payload,
        method: 'POST',
      })
      if (!response?.Success && !response?.success) {
        toast.error('Unable to create the order. Please try again.')
        return
      }
      dispatch(setOrderId(response.order_id))

      // Route to Scheduling only when at least one relevant location's cart
      // actually needs a kickoff/onboarding call (ported from the legacy
      // flow's shouldDisplayKickOffCall/comparePlans — see
      // shouldRequireOnboardingCall for the exact rule and its known gaps vs.
      // the legacy version). Decided PER LOCATION, not once for the combined
      // cart — for Multi-location Parent Pay, Scheduling itself shows one
      // calendar per location that individually needs a call (see
      // getSchedulingLocations/SchedulingStep).
      const schedulingLocations = getSchedulingLocations({
        locations: purchaseLinkLocations,
        orderMode,
        selectedLocationId,
        roles: effectiveRole,
        isTgpUpgradeEnabled,
      })
      const nextStepId = schedulingLocations.length
        ? 'scheduling'
        : 'payment-details'
      const nextStepIndex = STEPS.findIndex(s => s.id === nextStepId)
      dispatch(completeCurrentAndSetStep(nextStepIndex))
    } catch (err) {
      toast.error(err?.message || 'Unable to create the order.')
    } finally {
      setIsCreatingOrder(false)
    }
  }

  const handleSingleLocNext = async () => {
    if (!selectedCountry) {
      toast.error('Please select a country before continuing.')
      return
    }
    setIsSaving(true)
    try {
      // Load and validate the catalog before creating the account. Previously a
      // rejected KP inventory request was converted to null while account
      // creation still succeeded, which advanced to a permanently blank Core
      // Catalog. A failed inventory request is retryable and must keep the rep
      // on this step instead.
      // The default plan config is optional (null on failure), so it is fetched
      // alongside the catalog without gating account creation.
      let all5PlanPrices
      let defaultPlanConfig
      try {
        ;[all5PlanPrices, defaultPlanConfig] = await Promise.all([
          isKeapPartnerWorkflow
            ? fetchKeapPartnerCatalog(client, {
                country: selectedCountry || 'US',
                roles: effectiveRole,
              })
            : client('plans/5/v1/list'),
          client('plans/config/default').catch(() => null),
        ])
      } catch (catalogError) {
        toast.error(
          catalogError?.message ||
            'Unable to load the product catalog. Please try again.',
        )
        return
      }

      const response = await accountInfoRef.current?.saveAndContinue()
      if (!response?.success) {
        requestAnimationFrame(() => {
          const firstInvalid = document.querySelector('[aria-invalid="true"]')
          if (firstInvalid) {
            firstInvalid.scrollIntoView({behavior: 'smooth', block: 'center'})
            firstInvalid.focus({preventScroll: true})
          }
        })
      }

      if (response?.success) {
        // Attribute the just-created account to the partner NOW (at Continue), before
        // Generate Link. Without this, an account created but never Generate-Linked has
        // no partner association and can't appear in My Accounts. link_id 0 is a
        // "no link yet" sentinel (keap_partner_links ids are auto-increment from 1);
        // the backend upserts on the composite PK so retries are safe. Non-fatal — a
        // failure here must not block navigation; it only delays My Accounts visibility.
        try {
          if (keapPartnerId && user?.email && verifiedEmail) {
            await client('cc/order/keapClient/createClientPartnerMap', {
              data: {
                link_id: 0,
                keap_partner_id: keapPartnerId,
                keap_partner_email: user.email,
                keap_client_email: verifiedEmail,
                status: 'Created',
              },
            })
          }
        } catch (mapErr) {
          console.error('createClientPartnerMap (creation-time) failed', mapErr)
        }

        const newThryvId = response.childResponse?.childResponse?.thryvId
        if (
          await hasBlockingPastDueInvoice(client, newThryvId, selectedCountry)
        ) {
          setPastDueThryvId(newThryvId)
          setShowPastDueInvoiceModal(true)
          return
        }

        if (shouldUseLegacyPartnerOpsFlow(effectiveRoles)) {
          // Rollback only (REACT_APP_ENABLE_LEGACY_KP_CATALOG): PartnerOps
          // otherwise continues into the shared wizard below. A newly-created
          // single location is always a new order.
          const parentValues = parents?.[0]?.values ?? {}
          const childLocationValues = parents?.[0]?.children?.[0]?.values ?? {}
          const catalogValues =
            childLocationValues.businessName ||
            childLocationValues.firstName ||
            childLocationValues.lastName ||
            childLocationValues.locationName
              ? childLocationValues
              : parentValues
          const partnerOpsOrderState = buildNewLocationOrderState({
            response: {
              thryvId: newThryvId,
              ccId: response.parentResponse?.ccId,
            },
            parentAccount: {billTo: parentValues.billTo},
            editValues: catalogValues,
            country: selectedCountry || 'US',
            email: catalogValues.email || verifiedEmail,
            all5PlanPrices,
            defaultPlanConfig,
          })

          navigate('/order/new', {state: partnerOpsOrderState})
          return
        }

        if (
          keapPartnerId &&
          process.env.REACT_APP_ENABLE_LEGACY_KP_CATALOG === 'true'
        ) {
          const parentValues = parents?.[0]?.values ?? {}
          const childLocationValues = parents?.[0]?.children?.[0]?.values ?? {}
          const hasChildCatalogValues =
            childLocationValues.businessName ||
            childLocationValues.firstName ||
            childLocationValues.lastName ||
            childLocationValues.locationName
          const catalogValues = hasChildCatalogValues
            ? childLocationValues
            : parentValues
          const catalogBillTo =
            childLocationValues.billTo || parentValues.billTo || 'parent'
          const catalogLocationName =
            childLocationValues.locationName || parentValues.locationName || ''
          const catalogThryvId = response.childResponse?.childResponse?.thryvId
          navigate('/keap-partner-catalog', {
            state: {
              linkType: '1:1',
              isUpgrade: false,
              email: catalogValues.email || verifiedEmail,
              accountCode: catalogThryvId,
              selectedCountry: selectedCountry || 'US',
              keapPartnerId,
              // accountAlreadyCreated flag prevents triggerAccSetupApiCall from
              // calling accounts/5 again — parent+child were just created by Continue.
              thryvDetail: {
                thryvId: catalogThryvId,
                ccId: response.parentResponse?.ccId,
                accountAlreadyCreated: true,
                billTo: catalogBillTo,
              },
              accountInformation: {
                firstName: catalogValues.firstName || '',
                lastName: catalogValues.lastName || '',
                email: catalogValues.email || verifiedEmail || '',
                company: catalogValues.businessName || '',
                locationName: catalogLocationName,
                mobile: catalogValues.mobile || parentValues.mobile || '',
                country: selectedCountry || 'US',
                billTo: catalogBillTo,
                // kp-11Link-generateUrl.js reads accountInformation.abn for AU/NZ orders;
                // without this the ABN/NZBN keyed in on Account Setup never reaches the
                // purchase link, so it never reaches Recurly. Sibling of the same fix in
                // AccountInfo.js's handleOrder() and the Core OOE branch below. NZ orders
                // use the separate 'nzbn' field key (see config/locationFields.js).
                abn:
                  getTaxIdentifier(catalogValues) ||
                  getTaxIdentifier(parentValues),
                businessAddress: {
                  street:
                    childLocationValues.address || parentValues.address || '',
                  city: childLocationValues.city || parentValues.city || '',
                  state:
                    childLocationValues.state?.value ||
                    childLocationValues.state ||
                    parentValues.state?.value ||
                    parentValues.state ||
                    '',
                  postalCode:
                    childLocationValues.postCode || parentValues.postCode || '',
                  phone:
                    childLocationValues.telephone ||
                    parentValues.telephone ||
                    '',
                  country: selectedCountry || 'US',
                },
              },
              childCustomerInfo: {
                company: catalogValues.businessName || '',
                firstName: catalogValues.firstName || '',
                lastName: catalogValues.lastName || '',
                locationName: catalogLocationName,
                accountCode: catalogThryvId,
                billTo: catalogBillTo,
              },
            },
          })
          return
        }
        {
          // Core OOE: seed PurchaseLinkSlice with the newly created account
          // (as its sole location) and advance the wizard in place to the
          // Catalog Selection step (STEPS[2]) — no route change, unlike the
          // old '/order/new' hand-off this replaces. Checkout/link submission
          // is out of scope here too (see PurchaseLinkSlice/PurchaseLinkBuilder);
          // this only seeds enough state to browse the catalog and build a cart.
          const parentValues = parents?.[0]?.values ?? {}
          const childLocationValues = parents?.[0]?.children?.[0]?.values ?? {}
          const coreBillTo =
            childLocationValues.billTo || parentValues.billTo || 'parent'
          const countryIso = selectedCountry || 'US'
          // The account-creation response always splits parent vs. child/
          // location, even for a single-location signup (see the Keap-partner
          // branch above, which already reads these as two separate fields:
          // ccId: response.parentResponse?.ccId vs. catalogThryvId from
          // response.childResponse?.childResponse?.thryvId). The top-level
          // accountInfo below must use the PARENT's code — sourcing it from
          // the child instead would stamp order_header/send-link with the
          // location's id instead of the parent's, exactly the bug already
          // called out in AccountInfo.js's buildLocationEntry/handleOrder
          // comments for the multi-location flow.
          const locationThryvId = response.childResponse?.childResponse?.thryvId
          const parentThryvId = response.parentResponse?.ccId || locationThryvId
          const businessName = parentValues.businessName || ''

          // Every field besides thryvId is identical between the top-level
          // accountInfo and this sole location's own accountInfo — a
          // brand-new single-location signup has no separately-filled parent
          // vs. child form, just one set of values. Only thryvId differs:
          // the parent account's code at the top level, this location's own
          // id on the location entry (see the note above on parentThryvId
          // vs. locationThryvId).
          const sharedIdentity = {
            businessName,
            email: verifiedEmail,
            firstName: parentValues.firstName || null,
            lastName: parentValues.lastName || null,
            phone: parentValues.telephone || null,
            mobile: parentValues.mobile || null,
            country: countryIso,
            xpCode: parentValues.xpCode || childLocationValues.xpCode || null,
            salesRepOrPartner:
              parentValues.salesRepOrPartner ||
              childLocationValues.salesRepOrPartner ||
              null,
            billTo: coreBillTo,
            // Forwarded to order_header via buildCreateOrderPayload so the
            // payment page's "Same as my business address" checkbox has an
            // actual address to copy from — previously dropped here, leaving
            // order_header.company_address1/city/state/zipcode null.
            // A new one-location signup can retain its address on the child
            // location form. Keep the parent value when present, but fall
            // back to the child so the generated payment link is valid.
            address:
              parentValues.address || childLocationValues.address || null,
            city: parentValues.city || childLocationValues.city || null,
            state:
              parentValues.state?.value ||
              parentValues.state ||
              childLocationValues.state?.value ||
              childLocationValues.state ||
              null,
            postCode:
              parentValues.postCode || childLocationValues.postCode || null,
          }
          const accountInfoPayload = {
            thryvId: parentThryvId,
            keapPartnerId,
            ...sharedIdentity,
          }

          // Same child-preferred-fallback-to-parent pattern as xpCode/
          // salesRepOrPartner above, for the two fields that pattern doesn't
          // already cover — see PurchaseLinkSlice.js's salesAttributionFields.
          const salesAttributionFieldsPayload = {
            salesRepOrPartner: sharedIdentity.salesRepOrPartner,
            xpCode: sharedIdentity.xpCode,
            overrideSST:
              childLocationValues.overrideSST ||
              parentValues.overrideSST ||
              null,
            salesRepInfo:
              childLocationValues.salesRepInfo ||
              parentValues.salesRepInfo ||
              null,
            overrideRepInfo:
              childLocationValues.overrideRepInfo ||
              parentValues.overrideRepInfo ||
              null,
          }

          dispatch(
            setProductConfigInfo(
              applyProductRulesBeforeLoading(all5PlanPrices, userFeatures),
            ),
          )
          dispatch(setAccountInfo(accountInfoPayload))
          dispatch(setSalesAttributionFields(salesAttributionFieldsPayload))
          dispatch(
            initLocations({
              locations: [
                {
                  locationId: locationThryvId,
                  locationName: businessName,
                  thryvId: locationThryvId,
                  country: countryIso,
                  billTo: coreBillTo,
                  email: verifiedEmail,
                  accountInfo: {thryvId: locationThryvId, ...sharedIdentity},
                },
              ],
              selectedLocationId: locationThryvId,
            }),
          )
          dispatch(goNext())
        }
      }
    } finally {
      if (isMountedRef.current) setIsSaving(false)
    }
  }

  // Every step besides Account Info (which needs its own ref/lifecycle
  // props — see the isAccountInfoComponent branch below) just needs
  // whichever subset of these its own footer cares about; passing the
  // full set to all of them is harmless since each component only
  // destructures the props it actually uses.
  const extraStepProps =
    step?.id === 'catalog-selection'
      ? {
          onCancel: handleCancel,
          onContinue: handleCatalogContinue,
          canContinue: hasCartItems,
          isContinueLoading: isCreatingOrder,
        }
      : step?.id === 'email-lookup'
      ? {
          onCancel: handleCancel,
          showBackContinue: showNavButtons,
          onBack: () => dispatch(goBack()),
          canGoBack,
          onContinue: () => dispatch(goNext()),
          canProceed,
        }
      : {onCancel: handleCancel, onExitOrder: handleExit, isAdmin: isAdminRole} // scheduling, payment-details

  return (
    <PageWrapper>
      {showPastDueInvoiceModal &&
        (SHOW_INVOICE_INFO_FOR_PAST_DUE ? (
          <Modal
            altBtn={<></>}
            openOnLoad
            variant="lg"
            hideX
            onClose={() => setShowPastDueInvoiceModal(false)}
          >
            <InvoiceInformation
              thryvId={pastDueThryvId}
              email={verifiedEmail}
              selectedCountry={selectedCountry}
              setIsInvoiceBilled={() => {
                setShowPastDueInvoiceModal(false)
                handleCancel()
              }}
            />
          </Modal>
        ) : (
          <Modal
            altBtn={<></>}
            openOnLoad
            variant="lg"
            footer
            action
            btnAction={handleCancel}
            onClose={handleCancel}
            btnActionText="Ok"
            hideX
          >
            <div style={{padding: '20px', textAlign: 'center'}}>
              <strong>This account has a past due invoice.</strong>
              <br></br>
              Please resolve the outstanding balance before processing a new
              order.
            </div>
          </Modal>
        ))}
      {isSaving && !isValidatingAddress && (
        <LoadingOverlay>
          <SpinnerRing />
          <SpinnerLabel>Creating account…</SpinnerLabel>
        </LoadingOverlay>
      )}
      <Stepper
        currentStep={currentStep}
        onStepClick={index => {
          dispatch(setViewResultsEmail(null))
          dispatch(setStep(index))
        }}
      />

      <ContentAreaWrapper>
        <ContentArea fullWidth={isCatalogStep}>
          {viewResultsEmail ? (
            <>
              <StepCard>
                {keapPartnerId ? (
                  <MyAccounts
                    isKeapPartner
                    keapPartnerId={keapPartnerId}
                    initialSearch={viewResultsEmail}
                    initialThryvId={viewResultsThryvId}
                    initialAccountCodes={viewResultsAccountCodes}
                    onNewAccount={handleBackFromResults}
                    isEmbedded
                    onBack={handleBackFromResults}
                  />
                ) : (
                  <AllAccounts
                    initialSearch={viewResultsEmail}
                    isEmbedded
                    onBack={handleBackFromResults}
                    onAddAccount={handleAddAccountFromResults}
                  />
                )}
              </StepCard>

              {/* Not one of the wizard's own steps (it's an embedded search
                override), so it keeps the generic Cancel-only footer inline
                here rather than owning a footer component of its own. */}
              <FooterRow>
                <FooterCenter>
                  <Button
                    variant="secondary-gray"
                    size="sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </FooterCenter>
              </FooterRow>
            </>
          ) : isAccountInfoComponent ? (
            Component && (
              <Component
                {...stepProps}
                keapPartnerId={keapPartnerId}
                externalSaving={isSaving}
                onValidatingAddressChange={setIsValidatingAddress}
                ref={accountInfoRef}
                onReady={handleReady}
                onCancel={handleCancel}
                showBackContinue={showNavButtons && !showSinglelocaContinue}
                onBack={() => dispatch(goBack())}
                canGoBack={canGoBack}
                showContinue={showSinglelocaContinue}
                onContinue={
                  showSinglelocaContinue
                    ? handleSingleLocNext
                    : () => dispatch(goNext())
                }
                canContinue={canProceed}
                isContinueLoading={isSaving}
              />
            )
          ) : (
            // Every other step (email-lookup, scheduling, payment-details,
            // catalog-selection) now owns its own StepCard + footer card
            // internally — this just resolves Component/props through the
            // registry and renders it directly, no per-step wrapping or
            // layout branching left to do here.
            Component && (
              <Component
                {...stepProps}
                keapPartnerId={keapPartnerId}
                onReady={handleReady}
                {...extraStepProps}
              />
            )
          )}
        </ContentArea>
      </ContentAreaWrapper>
    </PageWrapper>
  )
}
